"use client";

import { useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import posthog from "posthog-js";
import { PostHogProvider as PHProvider } from "posthog-js/react";
import { useAuth } from "@/hooks/useAuth";
import { useMenuData } from "@/context/MenuDataContext";

const POSTHOG_HOST =
  process.env.NEXT_PUBLIC_POSTHOG_HOST ?? "https://eu.i.posthog.com";
const POSTHOG_UI_HOST = "https://eu.posthog.com";

/** A contact's page is addressed by an opaque id; links from before that carry the caller's
 *  phone number in the same segment, so either is scrubbed. */
const CONTACT_PHONE_PATH = /\/contacts\/[^/?#]+/;

/**
 * Removes personal data from a panel URL before it leaves the browser: the query string
 * and fragment (search terms), and the phone number a contact page is addressed by.
 */
export function scrubUrl(url: string): string {
  return url.split(/[?#]/)[0].replace(CONTACT_PHONE_PATH, "/contacts/[phone]");
}

const URL_PROPERTY = /url|pathname|referrer/i;

/**
 * `sanitize_properties` for every PostHog event: scrubs each URL-shaped property, and the
 * "initial" ones PostHog nests under `$set` / `$set_once` on a person.
 */
export function scrubUrlProperties(
  properties: Record<string, unknown>,
): Record<string, unknown> {
  const scrubbed: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(properties)) {
    if (typeof value === "string" && URL_PROPERTY.test(key)) {
      scrubbed[key] = scrubUrl(value);
    } else if ((key === "$set" || key === "$set_once") && isRecord(value)) {
      scrubbed[key] = scrubUrlProperties(value);
    } else {
      scrubbed[key] = value;
    }
  }
  return scrubbed;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Identifies the logged-in user in PostHog so feature flags evaluate by login
 * identity (not by anonymous device).
 *
 * Two steps, because client_id is not in the Cognito JWT (the backend resolves
 * it from PostgreSQL) and only reaches the frontend via MenuDataContext:
 *
 * 1. Auth-ready: identify(sub) + setPersonProperties({ email }) — flags evaluate
 *    per user, and can target named people by email rather than opaque subs.
 * 2. MenuData resolved: setPersonProperties({ client_id }) + group("client", id)
 *    + reloadFeatureFlags — enables flag targeting by client_id, both as a person
 *    property and as a PostHog group so client-group rollouts evaluate the same
 *    here and in the backend/voice-agent (which target groups={"client": id}).
 *
 * The email is set in step 1 rather than step 2 because it is available as soon
 * as auth is: deferring it would leave every email-targeted flag evaluating
 * against a user with no email until menu data resolves, and reading its default
 * in the meantime.
 *
 * distinct_id stays the Cognito sub (immutable) — the email is a person property,
 * so a changed address updates the same person rather than forking a new one.
 * Sending these three identifiers is disclosed in the Privacy Policy (§0 "Funciones
 * activas y uso del panel"): do not widen what is sent here without changing that
 * clause first.
 */
function PostHogIdentifier() {
  const { user } = useAuth();
  const { menuData } = useMenuData();
  const clientId = menuData?.client?.id;

  useEffect(() => {
    if (!user) return;
    posthog.identify(user.userId);
    // Deliberately not identify()'s second argument: with init() bootstrapped to
    // this same distinct_id, identify() is a no-op re-identify, and whether that
    // branch still applies its properties is an SDK internal. setPersonProperties
    // has no such branch.
    if (user.email) posthog.setPersonProperties({ email: user.email });
  }, [user]);

  useEffect(() => {
    if (!user || !clientId) return;
    posthog.setPersonProperties({ client_id: clientId });
    posthog.group("client", String(clientId));
    posthog.reloadFeatureFlags();
  }, [user, clientId]);

  return null;
}

/**
 * Pauses session replay while the route is a contact's page and resumes it after: that
 * page's URL is the caller's phone number, and the replay's own page metadata is the one
 * place the URL scrubber cannot reach.
 */
function ContactPageReplayPause() {
  const pathname = usePathname();
  const paused = useRef(false);

  useEffect(() => {
    if (!pathname) return;
    const onContactPage = CONTACT_PHONE_PATH.test(pathname);
    if (onContactPage && !paused.current) {
      posthog.stopSessionRecording();
      paused.current = true;
    } else if (!onContactPage && paused.current) {
      posthog.startSessionRecording();
      paused.current = false;
    }
  }, [pathname]);

  return null;
}

/**
 * PostHog for a signed-in user: feature flags, usage analytics, masked session replay and
 * surveys — what Privacy Policy §0 ("Funciones activas y uso del panel", legal 2.1.1)
 * discloses.
 *
 * Init waits for authentication so the login, signup and legal pages never touch PostHog
 * at all, and `persistence: "memory"` keeps it from writing cookies or localStorage on
 * the device (surveys remember what was answered in localStorage; the policy says so).
 *
 * The panel shows patient names, phone numbers and transcripts, so every text node,
 * element attribute and form input is masked and URLs are scrubbed. PostHog receives
 * layout and navigation, not patient data — the claim the sub-processor list (§7) makes.
 */
export function PostHogProvider({ children }: { children: React.ReactNode }) {
  const posthogKey = process.env.NEXT_PUBLIC_POSTHOG_KEY;
  const { user } = useAuth();
  // Gates mounting the children below until init() has actually run. Effects fire
  // child-before-parent, so if PostHogIdentifier mounted in the same commit as
  // this effect, its identify() call could reach posthog-js before init() does
  // and get silently dropped — flags then evaluate anonymously forever, matching
  // no per-user release condition. Mounting it one render later removes the race.
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (!posthogKey || !user) return;
    if (!posthog.__loaded) {
      posthog.init(posthogKey, {
        api_host: POSTHOG_HOST,
        ui_host: POSTHOG_UI_HOST,
        person_profiles: "identified_only",
        persistence: "memory",
        // The first /flags request goes out from init(), before identify() runs.
        // With persistence "memory" nothing carries over between page loads, so
        // without this it would be issued under a freshly minted anonymous id
        // that has no email and no client_id — every user-targeted flag reads
        // false until identify() lands and flags reload, which a component that
        // samples the flag once will miss. init() already waits for auth, so the
        // sub is available here: bootstrapping it makes the very first request an
        // identified one and leaves no anonymous window at all.
        bootstrap: { distinctID: user.userId, isIdentifiedID: true },
        // Usage analytics. "history_change" is what a Next.js App Router needs: one
        // $pageview per client-side navigation, not only on the initial load.
        autocapture: true,
        capture_pageview: "history_change",
        capture_pageleave: true,
        disable_session_recording: false,
        disable_surveys: false,
        // Patient-data masking — see the component doc.
        mask_all_text: true,
        mask_all_element_attributes: true,
        session_recording: {
          maskAllInputs: true,
          maskTextSelector: "*",
        },
        sanitize_properties: scrubUrlProperties,
      });
    }
    setInitialized(true);
  }, [posthogKey, user]);

  // Always provide the context so child hooks (useFeatureFlagEnabled) resolve
  // safely; identify only runs once PostHog is configured and initialized.
  return (
    <PHProvider client={posthog}>
      {posthogKey && initialized ? (
        <>
          <PostHogIdentifier />
          <ContactPageReplayPause />
        </>
      ) : null}
      {children}
    </PHProvider>
  );
}
